import { defineProperty as _defineProperty, objectSpread as _objectSpread, extends as _extends, inherits as _inherits, classCallCheck as _classCallCheck, possibleConstructorReturn as _possibleConstructorReturn, getPrototypeOf as _getPrototypeOf, assertThisInitialized as _assertThisInitialized, createClass as _createClass, objectWithoutProperties as _objectWithoutProperties } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { createRef, Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { withContext, marginBottomLevels, eventBus, getMarginBottomClass, IconChevronDown } from '@piggybank/core';
import { Consumer } from '../Field/context.js';
import { TextInput } from '../TextInput/TextInput.js';
import isBlurringOutsideRefs from '../../utils/isBlurringOutsideRefs.js';
import omit from 'lodash/fp/omit';
import isEmpty from 'lodash/fp/isEmpty';
import { parsePhoneNumberFromString, getCountryCallingCode } from 'libphonenumber-js';
import memoizeOne from 'memoize-one';
import countries from 'i18n-iso-countries';
import sortBy from 'lodash/fp/sortBy';
import { checkPartialNumberChangesCountry, getFormattedValues, countryCodes } from './utils.js';
import enLocale from 'i18n-iso-countries/langs/en.json';
import { parseCountry } from '../../utils/parseCountry.js';
import styles from './telephone-input.css.js';
var parsedEnCountry = parseCountry(enLocale);
countries.registerLocale(parsedEnCountry);
var memoizedParseNumber = memoizeOne(function (value) {
  return parsePhoneNumberFromString(value);
});

var getCountryCode = function getCountryCode(props) {
  var state = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var existingPhone = memoizedParseNumber(props.value.value);

  if (existingPhone && existingPhone.isValid()) {
    return existingPhone.country;
  }

  return state.countryCode || props.value.countryCode || props.countryCode;
};

var propTypes = {
  countryCode: PropTypes.string,
  countrySelect: PropTypes.bool,
  extendedValues: PropTypes.bool,
  locale: PropTypes.string,
  customModifier: PropTypes.func,
  countryLocaleData: PropTypes.object,

  /**
   * 0, 1, 2, 3, 4, 5, 6, 7
   */
  marginBottom: PropTypes.oneOf(marginBottomLevels),
  textMap: PropTypes.shape({
    selectCountry: PropTypes.node,
    enterTelephone: PropTypes.node,
    countryList: PropTypes.object
  }),

  /** docgen-from-context:<Field/> */
  value: PropTypes.shape({
    raw: PropTypes.string,
    value: PropTypes.string,
    countryCode: PropTypes.string
  }),

  /** docgen-from-context:<Field/> */
  name: PropTypes.string.isRequired,

  /** docgen-from-context:<Field/> */
  invalid: PropTypes.bool,

  /**
   * docgen-from-context:<Field/>
   * ---
   * proxied to eventBus
   */
  onChange: PropTypes.func,

  /** docgen-from-context:<Field/> */
  onBlur: PropTypes.func,

  /** docgen-from-context:<Field/> */
  describers: PropTypes.string,
  fullWidth: PropTypes.bool
};
var defaultProps = {
  countryCode: 'GB',
  countrySelect: true,
  extendedValues: false,
  locale: 'en',
  marginBottom: 0,
  customModifier: function customModifier(options) {
    return sortBy(['name'], options);
  },
  textMap: {
    selectCountry: 'Select a country',
    enterTelephone: 'Enter your telephone number',
    countryList: parsedEnCountry
  },
  fullWidth: false
};

var TelephoneInputInner = /*#__PURE__*/function (_Component) {
  _inherits(TelephoneInputInner, _Component);

  function TelephoneInputInner(props) {
    var _this;

    _classCallCheck(this, TelephoneInputInner);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(TelephoneInputInner).call(this, props));

    _defineProperty(_assertThisInitialized(_this), "handleCountryChange", function (event) {
      var value = _this.props.value;
      event.persist();

      _this.setState({
        countryCode: event.target.value
      }, function () {
        var updatedValue = value.raw.charAt(0) === '+' ? value.raw.substring(1, value.raw.length) : value.raw;

        _this.handleNumberChange({
          value: updatedValue,
          event: event
        });
      });
    });

    _defineProperty(_assertThisInitialized(_this), "hasCountryChanged", function (value) {
      // First, see if we can parse the number
      var phone = memoizedParseNumber(value); // if no phone number could be parsed

      if (phone) {
        // If so, and the country has changed then return new country code
        if (phone.isValid() && phone.isPossible() && _this.state.countryCode !== phone.country) {
          // We take the shortName from the lib as it can differentiate between countries when
          // the countryCallingCode is the same e.g. +1 for US and CA
          return {
            countryCode: phone.countryCallingCode,
            shortName: phone.country
          };
        } // If we can't parse the number then we try to determine the country calling code
        // from the partial number that has been input


        var match = checkPartialNumberChangesCountry(value, _this.state);
        if (match) return match; // When not using country select, default back to
        // countryCode prop for parsing telephone numbers
        // if parsing fails or country isn't valid

        if (!_this.props.countrySelect && !phone.country) {
          _this.setState({
            countryCode: _this.props.countryCode
          });
        }
      }

      return false;
    });

    _defineProperty(_assertThisInitialized(_this), "handleNumberChange", function (_ref) {
      var value = _ref.value,
          event = _ref.event;
      var extendedValues = _this.props.extendedValues;
      var countryCode = _this.state.countryCode; // Limit input to number characters and some symbols

      if (!/^[0-9+\-() ]*$/.test(value)) {
        return;
      }

      if (value === '') {
        return _this.handleChange({
          countryCode: countryCode,
          raw: '',
          value: ''
        }, event);
      }

      var changedCountry = _this.hasCountryChanged(value);

      if (changedCountry) {
        // Work out which country we've changed to, based on the country code and the details from libphonenumber-js
        _this.setState({
          countryCode: changedCountry.shortName
        });

        countryCode = changedCountry.shortName;
      }

      var formattedValue = getFormattedValues(value, countryCode, extendedValues);
      return _this.handleChange(formattedValue, event);
    });

    _defineProperty(_assertThisInitialized(_this), "handleChange", function (value, event) {
      eventBus.dispatch({
        type: 'onChange',
        component: 'TelephoneInput',
        value: value,
        name: _this.props.name
      });

      if (_this.props.onChange) {
        _this.props.onChange({
          value: value,
          event: event
        });
      }
    });

    _defineProperty(_assertThisInitialized(_this), "handleBlur", function (e) {
      if (_this.props.onBlur) {
        isBlurringOutsideRefs(e, _this.inputRefs).then(function (result) {
          if (result) _this.props.onBlur(e);
        });
      }
    });

    _defineProperty(_assertThisInitialized(_this), "getCountryOptions", function () {
      var countryOptions = countryCodes.map(function (country) {
        var countryName = countries.getName(country.shortName, _this.props.locale);

        if (!countryName) {
          countryName = "+".concat(country.countryCode);
        }

        return {
          name: countryName,
          shortName: country.shortName
        };
      });

      var options = _this.props.customModifier(countryOptions).map(function (country) {
        return React.createElement("option", {
          key: country.shortName,
          value: country.shortName
        }, country.name, " +", getCountryCallingCode(country.shortName));
      });

      return options;
    });

    var _countryCode = getCountryCode(props); // If only raw is set, calculate value and update


    if (!isEmpty(props.value.raw) && isEmpty(props.value.value)) {
      var formattedValues = getFormattedValues(props.value.raw, _countryCode, props.extendedValues);
      props.onChange({
        value: formattedValues
      });
    }

    _this.state = {
      countryCode: _countryCode,
      previousPropsValue: props.value.raw
    }; // Register language lookup based on prop

    if (props.textMap.countryList && props.locale !== 'en') {
      countries.registerLocale({
        locale: props.locale,
        countries: props.textMap.countryList
      });
    }

    if (props.countryLocaleData) {
      countries.registerLocale(props.countryLocaleData);
    }

    _this.inputRefs = {
      country: createRef(),
      telephone: createRef()
    };
    return _this;
  }

  _createClass(TelephoneInputInner, [{
    key: "render",
    value: function render() {
      var _omit = omit(['locale', 'countryLocaleData', 'countryCode', 'customModifier', 'extendedValues', 'value', 'onChange', 'onBlur'], this.props),
          countrySelect = _omit.countrySelect,
          forwardedRef = _omit.forwardedRef,
          invalid = _omit.invalid,
          marginBottom = _omit.marginBottom,
          textMap = _omit.textMap,
          name = _omit.name,
          fullWidth = _omit.fullWidth,
          rest = _objectWithoutProperties(_omit, ["countrySelect", "forwardedRef", "invalid", "marginBottom", "textMap", "name", "fullWidth"]);

      this.inputRefs.telephone = forwardedRef || this.inputRefs.telephone;
      return React.createElement("div", {
        className: cn(styles.TelephoneInput, getMarginBottomClass(marginBottom))
      }, countrySelect && React.createElement("div", {
        className: styles['Select-container']
      }, React.createElement("label", {
        className: styles.visuallyHidden,
        htmlFor: "".concat(name, "-country-select-field")
      }, textMap.selectCountry), React.createElement("select", {
        className: cn(styles['Select'], _defineProperty({}, styles['Select--invalid'], invalid)),
        name: "".concat(name, "-country-select"),
        id: "".concat(name, "-country-select-field"),
        onChange: this.handleCountryChange,
        onBlur: this.handleBlur,
        value: this.state.countryCode,
        ref: this.inputRefs.country
      }, this.getCountryOptions()), React.createElement("div", {
        "aria-hidden": "true",
        className: cn(styles['Value'], _defineProperty({}, styles['Value--invalid'], invalid)),
        role: "presentation"
      }, "+", getCountryCallingCode(this.state.countryCode)), React.createElement("div", {
        className: styles.Icon
      }, React.createElement(IconChevronDown, {
        marginBottom: 0
      }))), React.createElement("div", {
        className: cn(styles.Telephone, _defineProperty({}, styles.fullWidth, fullWidth))
      }, countrySelect && React.createElement("label", {
        className: styles.visuallyHidden,
        htmlFor: "".concat(name, "-telephone-field")
      }, textMap.enterTelephone), React.createElement(TextInput, _extends({
        fullWidth: true,
        invalid: invalid,
        onChange: this.handleNumberChange,
        onBlur: this.handleBlur,
        type: "tel",
        ref: this.inputRefs.telephone,
        name: countrySelect ? "".concat(name, "-telephone") : name,
        value: this.props.value.raw
      }, rest))));
    }
  }]);

  return TelephoneInputInner;
}(Component);

_defineProperty(TelephoneInputInner, "defaultProps", _objectSpread({}, defaultProps, {
  value: {
    raw: '',
    value: '',
    countryCode: ''
  }
}));

_defineProperty(TelephoneInputInner, "getDerivedStateFromProps", function (props, state) {
  if (props.value.raw !== state.previousPropsValue) {
    return {
      countryCode: getCountryCode(props, state),
      previousPropsValue: props.value.raw
    };
  }

  return null;
});

TelephoneInputInner.propTypes = process.env.NODE_ENV !== "production" ? _objectSpread({}, TextInput.propTypes, propTypes) : {};
var TelephoneInput = React.forwardRef(function (props, ref) {
  return React.createElement(TelephoneInputInner, _extends({}, props, {
    forwardedRef: ref
  }));
});
TelephoneInput.displayName = 'TelephoneInput';
TelephoneInput.propTypes = _objectSpread({}, TextInput.propTypes, propTypes);
TelephoneInput.defaultProps = defaultProps;
var TelephoneInput$1 = withContext(Consumer, function (value) {
  return {
    invalid: value.invalid,
    describers: value.describers,
    name: value.name,
    onBlur: value.onBlur,
    onChange: value.onChange,
    value: typeof value.value === 'string' ? {
      countryCode: '',
      raw: value.value,
      value: ''
    } : value.value
  };
})(TelephoneInput);
export default TelephoneInput$1;
export { TelephoneInput };